/**
 * Unified Notifications Hook
 * 
 * Replaces the complex session gap detection system with a single, reliable hook
 * that integrates with the unified event store backend.
 * 
 * Key Features:
 * - Smart aggregation for long offline periods (6+ hours or 20+ events)
 * - Real-time SSE streaming with automatic reconnection
 * - Position lifecycle tracking and correlation
 * - Persistent last-seen event tracking
 * - Enhanced error handling and recovery
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuthStore } from '../store/authStore';
import { useNotificationStore } from '../store/notificationStore';
import { notificationService } from '../services/notificationService';
import { unifiedActivityTracker } from '../services/unifiedActivityTracker';
import { recoveryOrchestrator } from '../services/recoveryOrchestrator';
import { getCurrentUserId } from './useUserContext';

// Global SSE connection management to prevent multiple connections
let globalSSEConnection: EventSource | null = null;
let globalInitializationPromise: Promise<void> | null = null;
let globalConnectionStatus: 'disconnected' | 'connecting' | 'connected' = 'disconnected';
let globalInitialized = false;
let globalLastSeenEventId: string | null = localStorage.getItem('lastSeenEventId');
let globalSessionStartTime = Date.now();
let globalReconnectAttempts = 0;
let activeHookInstances = new Set<() => void>(); // Track active hook instances for cleanup
let globalReconnectTimeout: NodeJS.Timeout | null = null;

// 🔥 CRITICAL FIX: Add global lock to prevent concurrent initializations
let globalInitializationLock = false;
const MAX_CONCURRENT_INIT_WAIT = 30000; // 30 seconds max wait for concurrent init

interface NotificationEvent {
  id: string;
  event_type: string;
  event_timestamp: number;
  data: any;
}

export const useUnifiedNotifications = () => {
  const { isApiAuthReady, accessToken, currentUserId } = useAuthStore();
  const { addNotification } = useNotificationStore();
  
  // State management (synced with global state)
  const [isInitialized, setIsInitialized] = useState(globalInitialized);
  const [connectionStatus, setConnectionStatus] = useState<'disconnected' | 'connecting' | 'connected'>(globalConnectionStatus);
  const [lastError] = useState<string | null>(null); // Removed setLastError as it's not used
  const [reconnectAttempts, setReconnectAttempts] = useState(globalReconnectAttempts);
  
  // Reference to this hook instance's cleanup function
  const cleanupFnRef = useRef<(() => void) | null>(null);
  
  // Constants
  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_DELAY = 5000; // 5 seconds
  
  /**
   * Initialize unified notifications system (global singleton)
   */
  const initializeNotifications = useCallback(async () => {
    // Generate unique ID for debugging this initialization attempt
    const attemptId = Math.random().toString(36).substr(2, 9);
    
    // 🔥 CRITICAL FIX: Check for concurrent initialization attempts
    if (globalInitializationLock) {
      console.log(`⏳ [${attemptId}] [Unified Notifications] Another initialization in progress, waiting...`);
      
      // Wait for the current initialization to complete, with timeout
      const startWait = Date.now();
      while (globalInitializationLock && (Date.now() - startWait) < MAX_CONCURRENT_INIT_WAIT) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      if (globalInitialized) {
        console.log(`✅ [${attemptId}] [Unified Notifications] Using existing initialization`);
        return;
      }
      
      if (globalInitializationLock) {
        console.warn(`⚠️ [${attemptId}] [Unified Notifications] Concurrent initialization timeout, proceeding anyway`);
      }
    }
    
    // Check if we're already initialized or initializing globally
    if (!isApiAuthReady || globalInitialized || globalInitializationPromise) {
      console.log(`🚫 [${attemptId}] [Unified Notifications] Skipping initialization:`, {
        isApiAuthReady,
        currentUserId,
        hasAccessToken: !!accessToken,
        globalInitialized,
        hasGlobalPromise: !!globalInitializationPromise,
        activeHookInstances: activeHookInstances.size,
        reason: !isApiAuthReady ? 'Auth not ready' : globalInitialized ? 'Already initialized' : 'Initialization in progress'
      });
      return globalInitializationPromise || Promise.resolve();
    }
    
    // Get fresh userId at initialization time (not from closure)
    const liveUserId = getCurrentUserId();
    
    console.log(`🔄 [${attemptId}] [Unified Notifications] Starting GLOBAL initialization`, {
      activeHooks: activeHookInstances.size,
      userId: liveUserId || 'unknown',
      hasToken: !!accessToken,
      isApiAuthReady
    });
    
    globalConnectionStatus = 'connecting';
    globalInitializationLock = true; // 🔥 Set lock before starting
    updateAllHookStates();
    
    // Create and store the global initialization promise
    globalInitializationPromise = (async () => {
      try {
        console.log('🔄 Initializing unified notifications...');
        
        // 🔥 CRITICAL FIX: Prepare deduplication service for recovery mode
        // This prevents stale deduplication data from blocking legitimate "while away" notifications
        const { milestoneDeduplicationService } = await import('../services/milestoneDeduplicationService');
        milestoneDeduplicationService.prepareForRecovery();
        
        // Start real-time SSE stream immediately
        await startSSEConnection();
        globalInitialized = true;
        globalConnectionStatus = 'connected';
        updateAllHookStates();
        
        // Update activity tracker connection status
        unifiedActivityTracker.setConnectionStatus(true);
        
        // Use recovery orchestrator for historical events (non-blocking)
        recoveryOrchestrator.orchestrateRecovery().then((result) => {
          console.log(`📥 Recovery orchestrator completed:`, result);
          
          if (result.success && result.eventsProcessed > 0) {
            console.log(`✅ Processed ${result.eventsProcessed} events via ${result.scenario} recovery, showed ${result.toastsShown} toasts`);
          } else if (!result.success && result.error) {
            console.warn('📭 Recovery orchestrator failed (live events will work):', result.error);
          }
        }).catch((error) => {
          console.warn('📭 Recovery orchestrator error (live events will work):', error.message || error);
        });
        
        globalReconnectAttempts = 0;
        updateAllHookStates();
        console.log('✅ Unified notifications initialized successfully');
        
      } catch (error) {
        console.error('❌ Failed to initialize notifications:', error);
        globalConnectionStatus = 'disconnected';
        updateAllHookStates();
        
        // Schedule retry
        scheduleReconnect();
        throw error;
      } finally {
        // 🔥 CRITICAL FIX: Clear both the promise and the lock when done
        globalInitializationPromise = null;
        globalInitializationLock = false;
      }
    })();
    
    return globalInitializationPromise;
  }, [isApiAuthReady]);

  /**
   * Update all active hook instances with current global state
   */
  const updateAllHookStates = () => {
    activeHookInstances.forEach(updateFn => {
      try {
        updateFn();
      } catch (error) {
        console.warn('Failed to update hook instance:', error);
      }
    });
  };




  
  /**
   * Process direct events (fast path - no storage, immediate UI updates)
   */
  const processDirectEvent = (event: NotificationEvent, eventUserId: string | null) => {
    try {
      console.log(`⚡ Processing direct ${event.event_type} event:`, event);
      
      switch (event.event_type) {
        case 'position_snapshot':
          // Handle position snapshots - update position cache immediately
          console.log('📸 Processing direct position snapshot:', event.data);
          
          // Dispatch custom event for position hooks to consume
          window.dispatchEvent(new CustomEvent('positionSnapshot', {
            detail: {
              positions: event.data.positions,
              timestamp: event.data.timestamp,
              userId: eventUserId || currentUserId || 'system',
              totalCount: event.data.total_count,
              source: event.data.source,
              metadata: {
                isRecoveryEvent: false, // Direct events are always live
                eventId: event.id,
                eventTimestamp: event.event_timestamp
              }
            }
          }));
          break;
          
        case 'position_pnl_change':
          // Handle PnL changes - update position cache, maybe show toast
          console.log('📊 Processing direct PnL change:', event.data);
          
          // Dispatch custom event for PnL updates
          // Note: Using snake_case to match PositionPnlChangeEvent type definition
          window.dispatchEvent(new CustomEvent('openPositionPnlChange', {
            detail: {
              symbol: event.data.symbol,
              account: event.data.account,
              side: event.data.side,
              exchange: event.data.exchange || 'bybit',
              old_pnl: event.data.old_pnl,
              new_pnl: event.data.new_pnl,
              pnl_change: event.data.pnl_change,
              mark_price: event.data.mark_price,
              timestamp: event.data.timestamp,
              user_id: eventUserId || currentUserId || 'system'
            }
          }));
          break;
          
        default:
          console.log(`⚠️ Unhandled direct event type: ${event.event_type}`);
      }
      
    } catch (error) {
      console.error('❌ Failed to process direct event:', error);
    }
  };

  /**
   * Process individual notification event (for short gaps)
   */
  const processNotificationEvent = (event: NotificationEvent, isRecovery = false) => {
    try {
      const isRecoveryEvent = isRecovery || event.event_timestamp < globalSessionStartTime;
      
      console.log(`📝 Processing ${event.event_type} event:`, event);
      
      // Create notification based on event type
      let notification = null;
      
      switch (event.event_type) {
        case 'trade_closed':
          notification = notificationService.createTradeClosedNotification({
            symbol: event.data.symbol,
            account: event.data.account,
            side: event.data.side,
            strategy: event.data.strategy,
            pnl: event.data.pnl,
            roi: event.data.roi_percentage,
            tradeId: event.data.trade_id,
            exectype: event.data.exectype,
            eventTimestamp: event.event_timestamp,
            isRecoveryEvent
          });
          break;
          
        case 'position_opened':
          // 🔥 SECURITY FIX: Use backend-provided user_id or current authenticated user
          // Get fresh userId from auth token to avoid stale closures
          const liveUserIdForOpened = getCurrentUserId();
          const positionUserId = event.data.user_id || liveUserIdForOpened;
          
          if (!positionUserId) {
            console.warn('⚠️ [Security] Position opened event missing user_id - skipping', {
              hasEventUserId: !!event.data.user_id,
              hasLiveUserId: !!liveUserIdForOpened,
              symbol: event.data.symbol
            });
            break;
          }
          
          // Dispatch custom event for position hooks
          window.dispatchEvent(new CustomEvent('openPositionOpened', {
            detail: {
              position: event.data, // Position data from backend
              symbol: event.data.symbol,
              account: event.data.account,
              side: event.data.side,
              exchange: 'bybit',
              pnl: event.data.unrealised_pnl || 0,
              qty: event.data.size || 0,
              mark_price: event.data.mark_price || 0,
              timestamp: event.event_timestamp.toString(),
              user_id: positionUserId // Use validated user ID, never 'system'
            }
          }));
          
          notification = notificationService.createPositionOpenedNotification({
            symbol: event.data.symbol,
            account: event.data.account,
            side: event.data.side,
            strategy: event.data.strategy,
            eventTimestamp: event.event_timestamp,
            isRecoveryEvent
          });
          break;
          
        case 'position_closed':
          // 🔥 SECURITY FIX: Use backend-provided or authenticated user_id
          // Get fresh userId from auth token to avoid stale closures
          const liveUserIdForClosed = getCurrentUserId();
          const closedUserId = event.data.user_id || liveUserIdForClosed;
          
          if (!closedUserId) {
            console.warn('⚠️ [Security] Position closed event missing user_id - skipping', {
              hasEventUserId: !!event.data.user_id,
              hasLiveUserId: !!liveUserIdForClosed,
              symbol: event.data.symbol
            });
            break;
          }
          
          // Dispatch custom event for position hooks
          window.dispatchEvent(new CustomEvent('openPositionClosed', {
            detail: {
              symbol: event.data.symbol,
              account: event.data.account,
              side: event.data.side,
              exchange: 'bybit',
              final_pnl: event.data.unrealised_pnl || 0,
              qty: event.data.size || 0,
              timestamp: event.event_timestamp.toString(),
              user_id: closedUserId
            }
          }));
          
          notification = notificationService.createPositionClosedNotification({
            symbol: event.data.symbol,
            account: event.data.account,
            side: event.data.side,
            finalPnl: event.data.final_pnl,
            strategy: event.data.strategy,
            tradeId: event.data.trade_id,
            eventTimestamp: event.event_timestamp,
            isRecoveryEvent
          });
          break;
          
        case 'position_modified':
          // 🔥 SECURITY FIX: Use backend-provided or authenticated user_id
          // Get fresh userId from auth token to avoid stale closures
          const liveUserIdForModified = getCurrentUserId();
          const modifiedUserId = event.data.user_id || liveUserIdForModified;
          
          if (!modifiedUserId) {
            console.warn('⚠️ [Security] Position modified event missing user_id - skipping', {
              hasEventUserId: !!event.data.user_id,
              hasLiveUserId: !!liveUserIdForModified,
              symbol: event.data.symbol
            });
            break;
          }
          
          // Dispatch custom event for position hooks
          window.dispatchEvent(new CustomEvent('openPositionModified', {
            detail: {
              symbol: event.data.symbol,
              account: event.data.account,
              side: event.data.side,
              exchange: 'bybit',
              old_qty: event.data.old_qty || 0,
              new_qty: event.data.new_qty || 0,
              qty_change: (event.data.new_qty || 0) - (event.data.old_qty || 0),
              pnl: event.data.unrealised_pnl || 0,
              mark_price: event.data.mark_price || 0,
              timestamp: event.event_timestamp.toString(),
              user_id: modifiedUserId
            }
          }));
          
          notification = notificationService.createPositionModifiedNotification({
            symbol: event.data.symbol,
            account: event.data.account,
            side: event.data.side,
            qtyChange: (event.data.new_qty || 0) - (event.data.old_qty || 0),
            strategy: event.data.strategy,
            eventTimestamp: event.event_timestamp,
            isRecoveryEvent,
            changes: event.data.changes
          });
          break;
          
        case 'position_milestone':
          // 🔥 SECURITY FIX: Use backend-provided or authenticated user_id
          // Get fresh userId from auth token to avoid stale closures
          const liveUserIdForMilestone = getCurrentUserId();
          const milestoneUserId = event.data.user_id || liveUserIdForMilestone;
          
          if (!milestoneUserId) {
            console.warn('⚠️ [Security] Position milestone event missing user_id - skipping', {
              hasEventUserId: !!event.data.user_id,
              hasLiveUserId: !!liveUserIdForMilestone,
              symbol: event.data.symbol
            });
            break;
          }
          
          // Dispatch custom event for position hooks
          window.dispatchEvent(new CustomEvent('openPositionMilestone', {
            detail: {
              symbol: event.data.symbol,
              account: event.data.account,
              side: event.data.side,
              exchange: 'bybit',
              milestone_percentage: event.data.milestone_percentage || 0,
              milestone_direction: event.data.milestone_direction || 'to_takeprofit',
              milestone_trend: event.data.milestone_trend || 'improving',
              previous_milestone_direction: event.data.previous_milestone_direction,
              time_since_last_crossing: event.data.time_since_last_crossing,
              current_pnl: event.data.current_pnl || 0,
              entry_price: event.data.entry_price || 0,
              mark_price: event.data.mark_price || 0,
              stop_loss: event.data.stop_loss,
              take_profit: event.data.take_profit,
              timestamp: event.event_timestamp.toString(),
              user_id: milestoneUserId,
              strategy: event.data.strategy,
              position: event.data.position
            }
          }));
          
          // Extract proper milestone data from stored event
          const storedMilestonePercentage = parseFloat(event.data.milestone_percentage) || 0;
          
          // 🔥 CRITICAL FIX: Only create notifications for ACTUAL milestone crossings during recovery
          // Skip recovery notifications if they don't represent actual crossing events
          if (isRecoveryEvent) {
            // Check if this represents an actual crossing by looking for crossing indicators
            const hasRealCrossingData = (
              event.data.time_since_last_crossing !== null && 
              event.data.time_since_last_crossing !== undefined
            ) || (
              event.data.previous_milestone_direction && 
              event.data.previous_milestone_direction !== event.data.milestone_trend
            );
            
            // If this is just a position state snapshot (not a real crossing), skip notification
            if (!hasRealCrossingData) {
              console.log(`🔄 [Recovery] Skipping state snapshot milestone (no crossing data): ${event.data.symbol} ${storedMilestonePercentage}%`);
              break; // Skip notification creation but still dispatch position update
            }
            
            // Check time-based filtering - only show milestones crossed within last 30 minutes
            const eventAge = Date.now() - event.event_timestamp;
            const thirtyMinutesMs = 30 * 60 * 1000;
            
            if (eventAge > thirtyMinutesMs) {
              console.log(`🔄 [Recovery] Skipping old milestone event: ${event.data.symbol} ${storedMilestonePercentage}% (${Math.round(eventAge / 60000)}min old)`);
              break; // Skip very old milestone events during recovery
            }
          }
          
          // Properly map milestone direction - negative percentages are declining, positive are improving
          const trendDirection = storedMilestonePercentage < 0 ? 'declining' : 'improving';
          
          console.log(`🎯 [${isRecoveryEvent ? 'Recovery' : 'Live'}] Creating milestone notification with percentage: ${storedMilestonePercentage}% for ${event.data.symbol}`);
          
          notification = notificationService.createMilestoneNotification({
            symbol: event.data.symbol,
            account: event.data.account,
            side: event.data.side,
            milestone_percentage: storedMilestonePercentage, // Use actual percentage from stored data
            currentPnl: event.data.current_pnl || event.data.unrealised_pnl,
            milestone_trend: trendDirection, // Use calculated trend based on percentage
            strategy: event.data.strategy,
            tradeId: event.data.trade_id,
            eventTimestamp: event.event_timestamp,
            isRecoveryEvent
          });
          break;
          
        case 'position_pnl_change':
          // Handle PnL change events - update cache and dispatch custom events
          console.log('💰 Processing position PnL change:', event.data);
          
          // 🔥 SECURITY FIX: Use backend-provided or authenticated user_id
          // Get fresh userId from auth token to avoid stale closures
          const liveUserIdForPnlChange = getCurrentUserId();
          const pnlChangeUserId = event.data.user_id || liveUserIdForPnlChange;
          
          if (!pnlChangeUserId) {
            console.warn('⚠️ [Security] Position PnL change event missing user_id - skipping', {
              hasEventUserId: !!event.data.user_id,
              hasLiveUserId: !!liveUserIdForPnlChange,
              symbol: event.data.symbol
            });
            break;
          }
          
          // Dispatch custom event for position hooks to consume
          window.dispatchEvent(new CustomEvent('openPositionPnlChange', {
            detail: {
              symbol: event.data.symbol,
              account: event.data.account,
              side: event.data.side,
              exchange: 'bybit', // Default exchange for now
              old_pnl: event.data.old_pnl,
              new_pnl: event.data.new_pnl,
              pnl_change: event.data.pnl_change,
              mark_price: event.data.mark_price,
              timestamp: event.data.timestamp.toString(),
              user_id: pnlChangeUserId // Use validated user ID
            }
          }));
          
          // Only create milestone notifications if this is an actual milestone event with percentage data
          // PnL change events should NOT be treated as milestones unless they have milestone_percentage
          const pnlMilestonePercentage = parseFloat(event.data.milestone_percentage);
          if (!isNaN(pnlMilestonePercentage) && Math.abs(event.data.pnl_change) > 10) {
            notification = notificationService.createMilestoneNotification({
              symbol: event.data.symbol,
              account: event.data.account,
              side: event.data.side,
              milestone_percentage: pnlMilestonePercentage,
              currentPnl: event.data.new_pnl,
              milestone_trend: event.data.pnl_change > 0 ? 'improving' : 'declining',
              tradeId: event.data.trade_id,
              eventTimestamp: event.event_timestamp,
              isRecoveryEvent
            });
          } else {
            // Skip creating milestone notifications for generic PnL changes
            console.log(`🔄 Skipping PnL change notification (not a milestone): ${event.data.symbol} ${event.data.pnl_change}`);
          }
          break;
          
        case 'position_snapshot':
          // Handle position snapshots - update position cache
          console.log('📸 Processing position snapshot:', event.data);
          
          // 🔥 SECURITY FIX: Use backend-provided user_id (already validated by SSE connection)
          // Never fallback to 'system' as it causes security validation failures
          // Get fresh userId from auth token to avoid stale closures
          const liveUserIdForSnapshot = getCurrentUserId();
          const snapshotUserId = event.data.user_id || liveUserIdForSnapshot;
          
          if (!snapshotUserId) {
            console.warn('⚠️ [Security] Position snapshot missing user_id - skipping', {
              hasEventUserId: !!event.data.user_id,
              hasLiveUserId: !!liveUserIdForSnapshot,
              totalCount: event.data.total_count
            });
            break;
          }
          
          // Dispatch custom event for position hooks to consume (single dispatch only)
          window.dispatchEvent(new CustomEvent('positionSnapshot', {
            detail: {
              positions: event.data.positions, // Direct access - matches expected format
              timestamp: event.data.timestamp,
              userId: snapshotUserId, // Use backend-validated user ID
              totalCount: event.data.total_count,
              source: event.data.source,
              metadata: {
                isRecoveryEvent,
                eventId: event.id,
                eventTimestamp: event.event_timestamp
              }
            }
          }));
          
          // Position snapshots are internal cache updates - no notifications needed
          // Backend creates position update notifications when appropriate
          console.log(`📸 Position snapshot processed: ${event.data.total_count} positions (source: ${event.data.source}, userId: ${snapshotUserId.substring(0, 20)}...)`);
          break;
          
        default:
          // 🔥 FIX: Don't create local notifications for unknown event types
          // Unknown events should be handled by backend or ignored gracefully
          console.log(`⚠️ Unhandled event type: ${event.event_type} - backend should handle all event types`);
          
          // Only dispatch UI events for position/trade updates that might need cache invalidation
          if (event.event_type.includes('position') || event.event_type.includes('trade')) {
            window.dispatchEvent(new CustomEvent('unknownTradingEvent', {
              detail: {
                eventType: event.event_type,
                data: event.data,
                timestamp: event.event_timestamp
              }
            }));
          }
          
          // Note: No notification created - backend should handle unknown event types
          // or events should be properly typed in the backend event system
          break;
      }
      
      if (notification) {
        addNotification(notification);
        
        // 🔥 DUPLICATE TOAST FIX: Removed showEventToast call to eliminate duplicate toasts
        // Enhanced toasts are now handled exclusively by App.tsx event handlers
        // This preserves notification panel functionality while removing duplicate basic toasts
      }
      
    } catch (error) {
      console.error('❌ Failed to process notification event:', error);
    }
  };

  /**
   * Start SSE connection for real-time events (global singleton)
   */
  const startSSEConnection = async () => {
    // 🔥 CRITICAL FIX: Prevent concurrent SSE connections
    if (globalSSEConnection) {
      console.log('🔄 Closing existing SSE connection before creating new one');
      globalSSEConnection.close();
      globalSSEConnection = null;
      // Small delay to ensure connection is fully closed
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    if (!accessToken) {
      console.error('❌ [SSE Connection] No access token available for SSE connection');
      globalConnectionStatus = 'disconnected';
      updateAllHookStates();
      throw new Error('No access token available for SSE connection');
    }

    try {
      // Get fresh userId at connection time (not from closure)
      const liveUserId = getCurrentUserId();
      
      console.log('🔗 [SSE Connection] Starting unified SSE connection...');
      console.log('🔗 [SSE Connection] User ID:', liveUserId || 'unknown');
      console.log('🔗 [SSE Connection] Token available:', !!accessToken);
      
      const apiUrl = import.meta.env.VITE_API_BASE_URL || '/api';
      const sseUrl = `${apiUrl}/events/stream?token=${encodeURIComponent(accessToken)}`;
      console.log('🔗 [SSE Connection] Connecting to:', sseUrl.replace(/token=[^&]+/, 'token=***'));
      
      const eventSource = new EventSource(sseUrl);
      
      eventSource.onopen = () => {
        // Get fresh userId for logging (not from closure)
        const liveUserId = getCurrentUserId();
        console.log('✅ [SSE Connection] Unified SSE connection opened successfully');
        console.log('✅ [SSE Connection] Ready to receive events for user:', liveUserId || 'unknown');
        globalConnectionStatus = 'connected';
        globalReconnectAttempts = 0;
        updateAllHookStates();
      };
      
      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'heartbeat') {
            console.log('💓 SSE heartbeat received');
            return;
          }
          
          if (data.type === 'connection_established') {
            console.log('🔗 SSE connection established:', data.connection_id);
            return;
          }
          
          // Handle direct events (fast path)
          if (data.type === 'position_snapshot') {
            console.log('📸 Processing direct position snapshot:', data.data);
            // Get currentUserId fresh from auth token (not from closure)
            const liveUserId = getCurrentUserId();
            processDirectEvent({
              id: `direct_${Date.now()}`,
              event_type: 'position_snapshot',
              event_timestamp: data.data.timestamp || Date.now(),
              data: data.data
            }, liveUserId || null);
            return;
          }
          
          if (data.type === 'open_position_pnl_change') {
            // Handle direct PnL changes (no notification, just position updates)
            console.log('📊 Processing direct PnL change:', data.data);
            // Get currentUserId fresh from auth token (not from closure)
            const liveUserId = getCurrentUserId();
            processDirectEvent({
              id: `direct_pnl_${Date.now()}`,
              event_type: 'position_pnl_change',
              event_timestamp: data.data.timestamp || Date.now(),
              data: data.data
            }, liveUserId || null);
            return;
          }
          
          // Handle unified notification events (stored events)
          if (data.type === 'unified_notification' && data.data) {
            // Process real-time event
            const eventData: NotificationEvent = {
              id: data.data.event_id,
              event_type: data.data.event_type,
              event_timestamp: data.data.event_timestamp,
              data: data.data.data
            };
            
            processNotificationEvent(eventData, false);
            updateLastSeenEventId(eventData.id);
          }
          
        } catch (error) {
          console.error('❌ Error processing SSE message:', error);
        }
      };
      
      eventSource.onerror = (error) => {
        console.error('❌ [SSE Connection] Connection error:', {
          error,
          readyState: eventSource.readyState,
          url: eventSource.url.replace(/token=[^&]+/, 'token=***'),
          reconnectAttempts: globalReconnectAttempts
        });
        
        // Only change status if we were previously connected or connecting
        if (globalConnectionStatus !== 'disconnected') {
          globalConnectionStatus = 'disconnected';
          updateAllHookStates();
        }
        
        // Schedule reconnect
        scheduleReconnect();
      };
      
      globalSSEConnection = eventSource;
      
    } catch (error) {
      console.error('❌ Failed to start SSE connection:', error);
      globalConnectionStatus = 'disconnected';
      updateAllHookStates();
      
      // Schedule reconnect
      scheduleReconnect();
    }
  };

  /**
   * Schedule reconnection attempt with exponential backoff (global)
   */
  const scheduleReconnect = () => {
    if (globalReconnectTimeout) {
      clearTimeout(globalReconnectTimeout);
      globalReconnectTimeout = null;
    }
    
    // Check if we've exceeded max attempts
    if (globalReconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.error(`❌ Max reconnection attempts (${MAX_RECONNECT_ATTEMPTS}) exceeded`);
      updateAllHookStates();
      return;
    }
    
    const delay = RECONNECT_DELAY * Math.pow(2, globalReconnectAttempts); // Exponential backoff
    
    globalReconnectTimeout = setTimeout(() => {
      if (isApiAuthReady && globalConnectionStatus !== 'connected') {
        console.log(`🔄 Attempting to reconnect unified notifications... (attempt ${globalReconnectAttempts + 1}/${MAX_RECONNECT_ATTEMPTS})`);
        globalReconnectAttempts++;
        globalInitialized = false;
        globalInitializationPromise = null;
        updateAllHookStates();
        initializeNotifications();
      }
    }, delay);
  };

  /**
   * Update last seen event ID (global)
   */
  const updateLastSeenEventId = (eventId: string) => {
    globalLastSeenEventId = eventId;
    unifiedActivityTracker.updateLastSeenEventId(eventId);
    console.log(`📌 Updated last seen event ID: ${eventId}`);
  };

  // 🔥 DUPLICATE TOAST FIX: Removed showEventToast function
  // This function was generating duplicate basic toasts that were inferior to the enhanced toasts
  // generated by App.tsx event handlers. The App.tsx toasts include:
  // - Account information [Account2]
  // - Strategy context (Bot22Heiken...)
  // - Better PnL formatting
  // - Click navigation to relevant charts
  // - Mobile-optimized display
  //
  // Notification panel functionality is preserved via addNotification() calls above.
  // Database storage continues via the backend SSE pipeline.
  // Enhanced toasts continue via App.tsx event listeners (openPositionMilestone, etc.)



  /**
   * Manual refresh function for user-triggered updates
   */
  const refreshNotifications = useCallback(async () => {
    if (!isApiAuthReady) return;
    
    try {
      console.log('🔄 Manual refresh triggered');
      
      // Use recovery orchestrator for manual refresh
      const result = await recoveryOrchestrator.orchestrateRecovery(true); // Force refresh
      
      if (result.success) {
        console.log(`✅ Manual refresh completed: ${result.eventsProcessed} events processed via ${result.scenario} recovery`);
      } else {
        console.warn('⚠️ Manual refresh had issues:', result.error);
        
        // Fallback to reinitializing if orchestrator fails
        globalInitialized = false;
        globalInitializationPromise = null;
        await initializeNotifications();
      }
    } catch (error) {
      console.error('❌ Manual refresh failed:', error);
      
      // Fallback to full reinitialization
      globalInitialized = false;
      globalInitializationPromise = null;
      await initializeNotifications();
    }
  }, [isApiAuthReady, initializeNotifications]);

  // Track user activity for better gap detection using unified tracker
  useEffect(() => {
    // The unified activity tracker handles all activity tracking
    // Just ensure it's initialized when the hook mounts
    unifiedActivityTracker.updateActivity();
    
    return () => {
      // Activity tracker handles its own cleanup
    };
  }, []);

  // Initialize when auth is ready
  useEffect(() => {
    console.log(`🔐 [Unified Notifications] Auth state changed:`, {
      isApiAuthReady,
      globalInitialized,
      hasGlobalPromise: !!globalInitializationPromise,
      willInitialize: isApiAuthReady && !globalInitialized && !globalInitializationPromise
    });
    
    if (isApiAuthReady && !globalInitialized && !globalInitializationPromise) {
      console.log(`🚀 [Unified Notifications] Starting initialization now that auth is ready!`);
      initializeNotifications();
    }
  }, [isApiAuthReady, initializeNotifications]);

  // Register this hook instance and cleanup on unmount
  useEffect(() => {
    // Generate unique ID for this hook instance
    const hookId = Math.random().toString(36).substr(2, 9);
    console.log(`🏗️ [Hook-${hookId}] Registering useUnifiedNotifications instance (Total instances will be: ${activeHookInstances.size + 1})`);
    
    // Create update function for this hook instance
    const updateThisHook = () => {
      setIsInitialized(globalInitialized);
      setConnectionStatus(globalConnectionStatus);
      setReconnectAttempts(globalReconnectAttempts);
    };
    
    // Register this hook instance
    activeHookInstances.add(updateThisHook);
    cleanupFnRef.current = updateThisHook;
    
    // Sync initial state
    updateThisHook();
    
    console.log(`📊 [Hook-${hookId}] Hook registered. Total active instances: ${activeHookInstances.size}`);
    
    return () => {
      console.log(`🗑️ [Hook-${hookId}] Unregistering hook instance. Instances before cleanup: ${activeHookInstances.size}`);
      
      // Unregister this hook instance
      activeHookInstances.delete(updateThisHook);
      
      console.log(`📊 [Hook-${hookId}] Hook unregistered. Remaining instances: ${activeHookInstances.size}`);
      
      // Only cleanup global resources if this is the last hook instance
      if (activeHookInstances.size === 0) {
        console.log(`🧹 [Hook-${hookId}] Last hook instance unmounting - cleaning up global resources`);
        if (globalSSEConnection) {
          globalSSEConnection.close();
          globalSSEConnection = null;
        }
        if (globalReconnectTimeout) {
          clearTimeout(globalReconnectTimeout);
          globalReconnectTimeout = null;
        }
        // Reset global state
        globalInitialized = false;
        globalInitializationPromise = null;
        globalInitializationLock = false; // 🔥 Clear lock on cleanup
        globalConnectionStatus = 'disconnected';
        globalReconnectAttempts = 0;
        
        // Update activity tracker connection status
        unifiedActivityTracker.setConnectionStatus(false);
        
        console.log(`🧹 [Hook-${hookId}] Global cleanup completed`);
      }
    };
  }, []);

  return {
    isInitialized,
    connectionStatus,
    lastError,
    lastSeenEventId: globalLastSeenEventId,
    reconnectAttempts,
    refreshNotifications
  };
};